/**
 * Device detection utilities for mobile/desktop detection
 */

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type Orientation = 'portrait' | 'landscape';

/**
 * Detect if the current device is mobile based on user agent and screen width
 * Returns true only for phones, false for tablets and desktops
 * Tablets should use desktop layout, not mobile layout
 */
export function isMobileDevice(): boolean {
  // Use getDeviceType as the source of truth
  // Only return true for actual mobile phones, not tablets
  return getDeviceType() === 'mobile';
}

/**
 * Detect device type (mobile, tablet, desktop)
 * Uses user agent first, then screen width as fallback
 */
export function getDeviceType(): DeviceType {
  if (typeof window === 'undefined') {
    return 'desktop';
  }

  const width = window.innerWidth;

  // Check user agent first for accurate device identification
  if (typeof navigator !== 'undefined') {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    // Check for iPad (tablet)
    if (/ipad/i.test(userAgent)) {
      return 'tablet';
    }
    
    // Check for Android tablets
    // Android tablets often have "tablet" in user agent, or are >= 600px wide
    if (/android/i.test(userAgent)) {
      // Check if it's explicitly a tablet in user agent
      if (/tablet/i.test(userAgent)) {
        return 'tablet';
      }
      // If screen is wide enough (>= 600px), it's likely a tablet
      // Android phones are typically < 600px wide
      if (width >= 600) {
        return 'tablet';
      }
      // Otherwise it's a phone
      return 'mobile';
    }
    
    // Check for mobile devices (iPhone, Android phones, etc.)
    const mobileRegex = /webos|iphone|ipod|blackberry|iemobile|opera mini/i;
    if (mobileRegex.test(userAgent)) {
      return 'mobile';
    }
  }

  // Fallback to screen width detection
  // Use more accurate breakpoints for tablet detection
  if (width < 600) {
    return 'mobile';
  } else if (width < 1024) {
    return 'tablet';
  }
  
  return 'desktop';
}

/**
 * Detect current orientation
 */
export function getOrientation(): Orientation {
  if (typeof window === 'undefined') {
    return 'portrait';
  }

  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
}

/**
 * Check if device is in portrait mode
 */
export function isPortrait(): boolean {
  return getOrientation() === 'portrait';
}

/**
 * Check if device is in landscape mode
 */
export function isLandscape(): boolean {
  return getOrientation() === 'landscape';
}

/**
 * Whether Family Device Mode toggle should be shown (any non-mobile: tablet or desktop)
 */
export function canShowFamilyMode(): boolean {
  return getDeviceType() !== 'mobile';
}
